
import axios from 'axios';
import { getFacilityUsers as getSecureFacilityUsers } from './userService';
import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Secure Facility Users Service
 * Uses backend API endpoints and secure server functions instead of direct Firestore access
 */

/**
 * Fetch users from the same facilities as the current user (Updated to use secure server functions)
 * @param {string} currentUserId - The current user's ID (kept for backward compatibility)
 * @returns {Promise<Array>} Array of users with their details
 */
export const getFacilityUsers = async (currentUserId) => {
  try {
    // Use secure server function to get user's accessible facilities
    const { getUserFacilities } = await import('./userService');
    const facilitiesResult = await getUserFacilities(1, 100); // Get up to 100 facilities
    
    if (!facilitiesResult.facilities || facilitiesResult.facilities.length === 0) {
      console.log('Current user has no facilities assigned');
      return [];
    }

    console.log('User facilities:', facilitiesResult.facilities.map(f => f.id));

    // For each facility, get users using the secure service
    const allFacilityUsers = [];
    
    for (const facility of facilitiesResult.facilities) {
      try {
        const facilityUsersResult = await getSecureFacilityUsers(facility.id);
        if (facilityUsersResult.users) {
          allFacilityUsers.push(...facilityUsersResult.users);
        }
      } catch (error) {
        console.warn(`Could not get users for facility ${facility.id}:`, error);
        // Continue with other facilities
      }
    }

    // Remove duplicates
    const uniqueUsers = allFacilityUsers.filter((user, index, self) => 
      index === self.findIndex(u => u.id === user.id)
    );

    console.log('Healthcare users from same facilities:', uniqueUsers);
    return uniqueUsers;

  } catch (error) {
    console.error('Error fetching facility users:', error);
    throw error;
  }
};

/**
 * Get the current user's details securely
 * @param {string} userId - The user's ID (kept for backward compatibility)
 * @returns {Promise<Object>} User details
 */
export const getCurrentUserDetails = async (userId) => {
  try {
    // Use secure server function to get user profile
    const { getUserProfile } = await import('./userService');
    const profileResult = await getUserProfile(userId);
    return profileResult.profile;
  } catch (error) {
    console.error('Error fetching current user details:', error);
    throw error;
  }
};

/**
 * Get users for the current user's default facility (optimized for UI components)
 * @param {string} defaultFacilityId - The user's default facility ID
 * @returns {Promise<Array>} Array of users for the default facility
 */
export const getUsersForDefaultFacility = async (defaultFacilityId) => {
  try {
    if (!defaultFacilityId) {
      console.log('No default facility provided');
      return [];
    }

    // Use secure server function to get users for specific facility
    const facilityUsersResult = await getSecureFacilityUsers(defaultFacilityId);
    return facilityUsersResult.users || [];
  } catch (error) {
    console.error('Error fetching users for default facility:', error);
    return []; // Return empty array instead of throwing to prevent UI crashes
  }
};

// =============================================================================
// SECURE DEPARTMENT AND ROOM MANAGEMENT
// =============================================================================
// These functions now use the secure facility API instead of direct Firestore

// Add Department
export async function addDepartment(facilityId, departmentData) {
  try {
    console.log('Adding department via API:', facilityId, departmentData.name);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/facility/facilities/${facilityId}/departments`,
      departmentData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.department;
  } catch (error) {
    console.error('Error adding department:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// Add Room
export async function addRoom(facilityId, departmentId, roomData) {
  try {
    console.log('Adding room via API:', facilityId, departmentId, roomData.name);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/facility/facilities/${facilityId}/departments/${departmentId}/rooms`,
      roomData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.room;
  } catch (error) {
    console.error('Error adding room:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// Update Department
export async function updateDepartment(facilityId, departmentId, departmentData) {
  try {
    console.log('Updating department via API:', facilityId, departmentId);
    const token = await getAuthToken();
    
    await axios.put(
      `${API_BASE}/facility/facilities/${facilityId}/departments/${departmentId}`,
      departmentData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error updating department:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// Update Room
export async function updateRoom(facilityId, departmentId, roomId, roomData) {
  try {
    console.log('Updating room via API:', facilityId, departmentId, roomId);
    const token = await getAuthToken();
    
    await axios.put(
      `${API_BASE}/facility/facilities/${facilityId}/departments/${departmentId}/rooms/${roomId}`,
      roomData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error updating room:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// Delete Department
export async function deleteDepartment(facilityId, departmentId) {
  try {
    console.log('Deleting department via API:', facilityId, departmentId);
    const token = await getAuthToken();
    
    await axios.delete(
      `${API_BASE}/facility/facilities/${facilityId}/departments/${departmentId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error deleting department:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// Delete Room
export async function deleteRoom(facilityId, departmentId, roomId) {
  try {
    console.log('Deleting room via API:', facilityId, departmentId, roomId);
    const token = await getAuthToken();
    
    await axios.delete(
      `${API_BASE}/facility/facilities/${facilityId}/departments/${departmentId}/rooms/${roomId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error deleting room:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// Get Departments for a facility
export async function getDepartments(facilityId) {
  try {
    console.log('Getting departments via API:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/facility/facilities/${facilityId}/departments`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.departments;
  } catch (error) {
    console.error('Error getting departments:', error);
    throw error;
  }
}

// Get Rooms for a facility (or specific department)
export async function getRooms(facilityId, departmentId = null) {
  try {
    console.log('Getting rooms via API:', facilityId, departmentId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    if (departmentId) {
      params.append('departmentId', departmentId);
    }
    
    const response = await axios.get(
      `${API_BASE}/facility/facilities/${facilityId}/rooms?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.rooms;
  } catch (error) {
    console.error('Error getting rooms:', error);
    throw error;
  }
}

// Get a specific room
export async function getRoom(facilityId, roomId) {
  try {
    console.log('Getting room via API:', facilityId, roomId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/facility/facilities/${facilityId}/rooms/${roomId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.room;
  } catch (error) {
    console.error('Error getting room:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// =============================================================================
// ADDITIONAL HELPER FUNCTIONS
// =============================================================================

/**
 * Get facility structure (departments and rooms) for a facility
 */
export async function getFacilityStructure(facilityId) {
  try {
    console.log('Getting facility structure via API:', facilityId);
    
    const departments = await getDepartments(facilityId);
    
    // Get rooms for each department
    const departmentsWithRooms = await Promise.all(
      departments.map(async (dept) => {
        try {
          const rooms = await getRooms(facilityId, dept.id);
          return { ...dept, rooms };
        } catch (error) {
          console.warn(`Could not get rooms for department ${dept.id}:`, error);
          return { ...dept, rooms: [] };
        }
      })
    );
    
    return departmentsWithRooms;
  } catch (error) {
    console.error('Error getting facility structure:', error);
    throw error;
  }
}

/**
 * Search rooms across all departments in a facility
 */
export async function searchRooms(facilityId, searchTerm) {
  try {
    console.log('Searching rooms via API:', facilityId, searchTerm);
    
    const allRooms = await getRooms(facilityId);
    
    if (!searchTerm || searchTerm.trim() === '') {
      return allRooms;
    }
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return allRooms.filter(room => 
      (room.name && room.name.toLowerCase().includes(lowerSearchTerm)) ||
      (room.code && room.code.toLowerCase().includes(lowerSearchTerm)) ||
      (room.type && room.type.toLowerCase().includes(lowerSearchTerm))
    );
  } catch (error) {
    console.error('Error searching rooms:', error);
    throw error;
  }
}

/**
 * Get departments by type
 */
export async function getDepartmentsByType(facilityId, departmentType) {
  try {
    const departments = await getDepartments(facilityId);
    return departments.filter(dept => 
      dept.type === departmentType || 
      dept.category === departmentType
    );
  } catch (error) {
    console.error('Error getting departments by type:', error);
    throw error;
  }
}

/**
 * Get rooms by type
 */
export async function getRoomsByType(facilityId, roomType) {
  try {
    const rooms = await getRooms(facilityId);
    return rooms.filter(room => room.type === roomType);
  } catch (error) {
    console.error('Error getting rooms by type:', error);
    throw error;
  }
}

/**
 * Validate room capacity
 */
export async function validateRoomCapacity(facilityId, roomId, requiredCapacity) {
  try {
    const room = await getRoom(facilityId, roomId);
    if (!room) {
      return { valid: false, reason: 'Room not found' };
    }
    
    if (!room.capacity || room.capacity < requiredCapacity) {
      return { 
        valid: false, 
        reason: `Room capacity (${room.capacity || 0}) is less than required (${requiredCapacity})` 
      };
    }
    
    return { valid: true };
  } catch (error) {
    console.error('Error validating room capacity:', error);
    return { valid: false, reason: 'Error validating capacity' };
  }
}

/**
 * Get facility statistics
 */
export async function getFacilityStats(facilityId) {
  try {
    console.log('Getting facility statistics via API:', facilityId);
    
    const departments = await getDepartments(facilityId);
    const allRooms = await getRooms(facilityId);
    
    const stats = {
      totalDepartments: departments.length,
      totalRooms: allRooms.length,
      roomsByType: {},
      departmentsByType: {},
      avgRoomsPerDepartment: departments.length > 0 ? (allRooms.length / departments.length).toFixed(1) : 0
    };
    
    // Count rooms by type
    allRooms.forEach(room => {
      const type = room.type || 'Unknown';
      stats.roomsByType[type] = (stats.roomsByType[type] || 0) + 1;
    });
    
    // Count departments by type
    departments.forEach(dept => {
      const type = dept.type || dept.category || 'Unknown';
      stats.departmentsByType[type] = (stats.departmentsByType[type] || 0) + 1;
    });
    
    return stats;
  } catch (error) {
    console.error('Error getting facility statistics:', error);
    throw error;
  }
}

/**
 * Bulk operations for departments and rooms
 */
export async function bulkCreateRooms(facilityId, departmentId, roomsData) {
  try {
    console.log('Bulk creating rooms via API:', facilityId, departmentId, roomsData.length);
    
    const promises = roomsData.map(roomData => 
      addRoom(facilityId, departmentId, roomData)
    );
    
    const results = await Promise.allSettled(promises);
    
    const summary = {
      successful: 0,
      failed: 0,
      rooms: [],
      errors: []
    };
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        summary.successful++;
        summary.rooms.push(result.value);
      } else {
        summary.failed++;
        summary.errors.push({
          roomData: roomsData[index],
          error: result.reason
        });
      }
    });
    
    return summary;
  } catch (error) {
    console.error('Error bulk creating rooms:', error);
    throw error;
  }
}
